import { Loader2, Sparkles } from 'lucide-preact'
import type { ErrorSummaryState } from './useErrorSummary'

/**
 * Renders the AI error summary card in its three states: loading skeleton, resolved
 * summary, or failure fallback. Returns null when idle (no summary, not loading, not
 * failed) so the surrounding layout collapses cleanly.
 */
export function ErrorSummarySection({ summary }: { summary: ErrorSummaryState }) {
  // Reserve the card-shaped slot eagerly so the summary arriving later doesn't push the
  // error list down — avoids layout shift during the fetch.
  if (summary.loading) {
    return (
      <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
        <div className="flex items-center gap-1.5 mb-1.5 font-medium text-blue-700 dark:text-blue-300">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          AI Summary
        </div>
        <div className="space-y-1.5" aria-hidden="true">
          <div className="h-2.5 rounded bg-blue-200/70 dark:bg-blue-800/50 animate-pulse" />
          <div className="h-2.5 rounded bg-blue-200/70 dark:bg-blue-800/50 animate-pulse w-11/12" />
          <div className="h-2.5 rounded bg-blue-200/70 dark:bg-blue-800/50 animate-pulse w-4/5" />
        </div>
        <span className="sr-only">Generating summary…</span>
      </div>
    )
  }

  if (summary.aiSummary) {
    return (
      <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
        <div className="flex items-center gap-1.5 mb-1.5 font-medium text-blue-700 dark:text-blue-300">
          <Sparkles className="w-3.5 h-3.5" />
          AI Summary
        </div>
        {summary.aiSummary}
      </div>
    )
  }

  // Failure fallback — only show when the fetch actually failed, not on the ambiguous
  // pre-effect initial render. Copy references "below" because the summary slot now sits
  // above the error list.
  if (summary.failed) {
    return (
      <p className="text-xs text-slate-500 dark:text-slate-400 italic">
        Unable to generate summary. Please review the errors below.
      </p>
    )
  }

  return null
}
